import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, DatePicker, Button, message, Row, Col } from 'antd';
import { CalendarOutlined, SaveOutlined } from '@ant-design/icons';
import WorkPlanService from '../services/Workplanservice';
import moment from 'moment';

const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

/**
 * WorkPlanForm Component
 * Form for creating and editing work plans
 * 
 * Props:
 * - visible: boolean - Show/hide modal
 * - onCancel: function - Close modal handler
 * - onSuccess: function - Success callback (refresh list)
 * - editData: object - Work plan data for editing (null for create)
 */
const WorkPlanForm = ({ visible, onCancel, onSuccess, editData = null }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [regions] = useState([
        'Hhohho',
        'Manzini',
        'Shiselweni',
        'Lubombo'
    ]);

    // Populate form when editing
    useEffect(() => {
        if (editData && visible) {
            form.setFieldsValue({
                name: editData.name,
                region: editData.region,
                dateRange: [
                    moment(editData.start_date),
                    moment(editData.end_date)
                ],
                status: editData.status,
                description: editData.description
            });
        } else if (visible) {
            form.resetFields();
        }
    }, [editData, visible, form]);

    const handleSubmit = async (values) => {
        setLoading(true);
        
        try {
            const data = {
                name: values.name,
                region: values.region,
                start_date: values.dateRange[0].format('YYYY-MM-DD'),
                end_date: values.dateRange[1].format('YYYY-MM-DD'),
                status: values.status,
                description: values.description || ''
            };

            if (editData) {
                // Update existing work plan
                await WorkPlanService.updateWorkPlan(editData.id, data);
                message.success('Work plan updated successfully!');
            } else {
                // Create new work plan
                await WorkPlanService.createWorkPlan(data);
                message.success('Work plan created successfully!');
            }

            form.resetFields();
            onSuccess();
            onCancel();
            
        } catch (error) {
            console.error('Error saving work plan:', error);
            message.error(error.response?.data?.message || 'Failed to save work plan');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    return (
        <Modal
            title={
                <span>
                    <CalendarOutlined style={{ marginRight: 8 }} />
                    {editData ? 'Edit Work Plan' : 'Create New Work Plan'}
                </span>
            }
            visible={visible}
            onCancel={handleCancel}
            footer={null}
            width={700}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                    status: 'planned',
                    region: null
                }}
            >
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            label="Work Plan Name"
                            name="name"
                            rules={[
                                { required: true, message: 'Please enter work plan name' },
                                { min: 5, message: 'Name must be at least 5 characters' },
                                { max: 200, message: 'Name must not exceed 200 characters' }
                            ]}
                        >
                            <Input 
                                placeholder="e.g., Q1 2025 Asset Verification Program"
                                size="large"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Region"
                            name="region"
                            rules={[{ required: true, message: 'Please select a region' }]}
                        >
                            <Select 
                                placeholder="Select region"
                                size="large"
                                allowClear
                            >
                                {regions.map(region => (
                                    <Option key={region} value={region}>
                                        {region}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Status"
                            name="status"
                            rules={[{ required: true, message: 'Please select status' }]}
                        >
                            <Select size="large">
                                <Option value="planned">Planned</Option>
                                <Option value="in_progress">In Progress</Option>
                                <Option value="completed">Completed</Option>
                                <Option value="cancelled">Cancelled</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            label="Work Plan Duration"
                            name="dateRange"
                            rules={[
                                { required: true, message: 'Please select start and end dates' }
                            ]}
                        >
                            <RangePicker 
                                size="large"
                                style={{ width: '100%' }}
                                format="DD MMM YYYY"
                                disabledDate={(current) => {
                                    // Disable dates before today (for new work plans)
                                    if (!editData) {
                                        return current && current < moment().startOf('day');
                                    }
                                    return false;
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            label="Description & Objectives"
                            name="description"
                            rules={[
                                { max: 1000, message: 'Description must not exceed 1000 characters' }
                            ]}
                        >
                            <TextArea 
                                rows={4}
                                placeholder="Describe the objectives and scope of this work plan..."
                                showCount
                                maxLength={1000}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={24}>
                        <div style={{ 
                            background: '#f0f2f5', 
                            padding: '12px', 
                            borderRadius: '4px',
                            marginBottom: '16px'
                        }}>
                            <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                                <strong>Note:</strong> After creating the work plan, you can schedule 
                                facility visits and assign team members from the main dashboard.
                            </p>
                        </div>
                    </Col>
                </Row>

                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                    <Button onClick={handleCancel} style={{ marginRight: 8 }}>
                        Cancel
                    </Button>
                    <Button 
                        type="primary" 
                        htmlType="submit" 
                        loading={loading}
                        icon={<SaveOutlined />}
                    >
                        {editData ? 'Update Work Plan' : 'Create Work Plan'}
                    </Button>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default WorkPlanForm;